import type { BrandDirectoryRow, OrganizationDirectoryRow } from '../../services/brandDirectoryApi';

export interface ViewAsTarget {
  rowKey: string;
  id: string;
  name: string;
  /** Organizations impersonate as themselves; brands go through the brand path. */
  kind: 'organization' | 'brand';
  type: 'store' | 'vendor';
  status: 'active' | 'inactive' | 'pending';
  meta: string;
  /** Lowercased haystack: name, claimed brands, aliases, slug, type. */
  searchText: string;
  /** Legal org name when the row is a claimed brand pointing at its owner. */
  impersonationName?: string;
}

export interface ViewAsLabels {
  store: string;
  vendor: string;
  brand: string;
  pending: string;
}

/**
 * The backend spells "pending" differently per organization type
 * (`pending_approval` for vendors, `pending_verification` for stores). Both are
 * legitimately viewable — inspecting an account mid-onboarding is a large part
 * of why View-as exists — so only genuinely dormant accounts are excluded.
 */
export const UNAVAILABLE_ORG_STATUSES = ['inactive', 'suspended', 'rejected', 'denied'];

export const normalizeOrgStatus = (status: unknown): ViewAsTarget['status'] => {
  const value = typeof status === 'string' ? status.toLowerCase() : '';
  if (value.startsWith('pending')) return 'pending';
  if (value === 'inactive' || value === 'suspended') return 'inactive';
  return 'active';
};

const isUnavailableOrg = (status: unknown): boolean =>
  UNAVAILABLE_ORG_STATUSES.includes(String(status || '').toLowerCase());

const orgMeta = (type: ViewAsTarget['type'], status: ViewAsTarget['status'], labels: ViewAsLabels): string =>
  status === 'pending' ? `${labels[type]} · ${labels.pending}` : labels[type];

const haystack = (...parts: Array<string | null | undefined>): string =>
  parts
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(' ')
    .toLowerCase();

/**
 * The same impersonation target (kind + id) reached under the same display
 * name is one account, however many sources surfaced it. A claimed brand whose
 * name matches its owning organization ("ProMaster" the brand, "ProMaster" the
 * vendor org) resolves to that organization's id, so it used to render as a
 * second, indistinguishable row.
 */
const dedupeKey = (target: ViewAsTarget): string =>
  `${target.kind}:${target.id}:${target.name.trim().toLowerCase()}`;

const dedupeTargets = (targets: ViewAsTarget[]): ViewAsTarget[] => {
  const byKey = new Map<string, ViewAsTarget>();
  targets.forEach((target) => {
    const key = dedupeKey(target);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...target });
      return;
    }
    // Keep the first row (organizations are listed before brands) but stay
    // searchable by everything the duplicate knew about.
    existing.searchText = `${existing.searchText} ${target.searchText}`;
  });
  return Array.from(byKey.values());
};

/**
 * Genuinely different accounts can still share a display name. Give those a
 * short id suffix so the admin never has to guess which one they are about to
 * impersonate.
 */
const disambiguateNames = (targets: ViewAsTarget[]): ViewAsTarget[] => {
  const counts = new Map<string, number>();
  targets.forEach((target) => {
    const key = target.name.trim().toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return targets.map((target) =>
    (counts.get(target.name.trim().toLowerCase()) || 0) > 1
      ? { ...target, meta: `${target.meta} · #${target.id.slice(0, 6)}` }
      : target,
  );
};

/**
 * Build the View-as roster.
 *
 * Confirmed claims are hidden from the brand-only path (that scope is narrower
 * than the owning vendor org). They still appear as their own rows so Alex can
 * type "Canon" and land on the vendor that claimed it. Requested claims stay
 * on the requesting org via `brandsClaimed` — they do not get a brand row.
 * A denied or missing owner does not hide the brand: View as falls back to
 * the brand dashboard so a rejected Gmail test cannot bury Canon.
 */
export const buildViewAsTargets = (
  organizations: OrganizationDirectoryRow[] | undefined,
  brands: BrandDirectoryRow[] | undefined,
  labels: ViewAsLabels,
): ViewAsTarget[] => {
  const orgTargets: ViewAsTarget[] = (organizations || [])
    .filter((org) => org?.id && org?.name)
    .filter((org) => !isUnavailableOrg(org.status))
    .map((org) => {
      const type: 'store' | 'vendor' = org.orgType === 'vendor' ? 'vendor' : 'store';
      const status = normalizeOrgStatus(org.status);
      const meta = orgMeta(type, status, labels);
      const claimedNames = (org.brandsClaimed || []).flatMap((brand) => [
        brand.brandName,
        brand.brandId,
      ]);
      return {
        rowKey: `org-${org.id}`,
        id: org.id,
        name: org.name,
        kind: 'organization' as const,
        type,
        status,
        meta,
        searchText: haystack(org.name, org.adminEmail, meta, ...claimedNames),
      };
    });

  const unavailableOwnerIds = new Set(
    (organizations || [])
      .filter((org) => org?.id && isUnavailableOrg(org.status))
      .map((org) => org.id),
  );
  const orgById = new Map((organizations || []).filter((org) => org?.id).map((org) => [org.id, org]));

  const brandTargets: ViewAsTarget[] = (brands || [])
    .filter((brand) => Boolean(brand?.id && brand?.name && brand.status !== 'inactive'))
    .flatMap((brand): ViewAsTarget[] => {
      const ownerId = brand.claimedBy?.organizationId;
      const aliases = brand.aliases || [];

      const brandOnly = (): ViewAsTarget[] => {
        const meta = labels.brand;
        return [
          {
            rowKey: `brand-${brand.id}`,
            id: brand.id,
            name: brand.name,
            kind: 'brand' as const,
            type: 'vendor' as const,
            status: 'active' as const,
            meta,
            searchText: haystack(brand.name, brand.slug, meta, ...aliases),
          },
        ];
      };

      if (!ownerId) {
        return brandOnly();
      }

      // Pending claims already surface through the requesting org's brandsClaimed
      // search. Do not impersonate that org as if the brand were approved.
      if (brand.claimedBy?.status !== 'claimed') {
        return [];
      }

      const owner = orgById.get(ownerId);
      // A denied or deleted claimant is not a viewable vendor account. Keep the
      // brand itself on the roster so Alex can still open the brand dashboard
      // without inheriting that person's org.
      if (!owner || brand.claimedBy?.ownerSuspended || unavailableOwnerIds.has(ownerId)) {
        return brandOnly();
      }

      const status = normalizeOrgStatus(owner.status);
      // A store that owns a claim is still a store. View-as must not turn it
      // into a brand account.
      const ownerType: 'store' | 'vendor' = owner.orgType === 'vendor' ? 'vendor' : 'store';
      const meta = orgMeta(ownerType, status, labels);
      const ownerName = brand.claimedBy.organizationName || owner.name;
      // When the brand is named differently from the account it resolves to,
      // say which account that is.
      const displayMeta =
        ownerName && ownerName.trim().toLowerCase() !== brand.name.trim().toLowerCase()
          ? `${meta} · ${ownerName}`
          : meta;

      return [
        {
          rowKey: `brand-${brand.id}`,
          id: ownerId,
          name: brand.name,
          kind: 'organization' as const,
          type: ownerType,
          status,
          meta: displayMeta,
          impersonationName: ownerName || brand.name,
          searchText: haystack(brand.name, brand.slug, meta, ownerName, owner?.name, ...aliases),
        },
      ];
    });

  return dedupeTargets([...orgTargets, ...brandTargets]);
};

export const matchesViewAsQuery = (target: ViewAsTarget, needle: string): boolean => {
  if (!needle) return true;
  return target.searchText.includes(needle) || target.name.toLowerCase().includes(needle);
};

const WORD_SPLIT = /[\s\-_.,&/()]+/;

/**
 * Lower is better: exact name (0) > name starts with (1) > a word in the name
 * starts with (2) > name contains (3) > matched only through brands, aliases,
 * email or type (4).
 */
export const viewAsMatchRank = (target: ViewAsTarget, needle: string): number => {
  const name = target.name.trim().toLowerCase();
  if (name === needle) return 0;
  if (name.startsWith(needle)) return 1;
  const padded = ` ${name.split(WORD_SPLIT).filter(Boolean).join(' ')}`;
  const wordNeedle = needle.split(WORD_SPLIT).filter(Boolean).join(' ');
  if (wordNeedle && padded.includes(` ${wordNeedle}`)) return 2;
  if (name.includes(needle)) return 3;
  return 4;
};

const compareNames = (a: ViewAsTarget, b: ViewAsTarget): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.rowKey.localeCompare(b.rowKey);

export type ViewAsGroup = 'accounts' | 'brands';

/**
 * An account is an `organizations` doc the roster did not exclude as dormant,
 * reached directly or through a brand it claimed. Everything else is a brand
 * with no account yet, viewable through the brand path. Member counts play no
 * part: `userCount` is read from the legacy membership mirrors, so a false zero
 * would bury a paying store, and a store Motzu onboarded by hand before inviting
 * anyone is exactly who support needs to open.
 */
export const viewAsGroup = (target: ViewAsTarget): ViewAsGroup =>
  target.kind === 'organization' ? 'accounts' : 'brands';

const compareGroups = (a: ViewAsTarget, b: ViewAsTarget): number =>
  Number(viewAsGroup(a) === 'brands') - Number(viewAsGroup(b) === 'brands');

/** What a row impersonates: one account, whichever name it was reached under. */
export const viewAsAccountKey = (target: ViewAsTarget): string => `${target.kind}:${target.id}`;

const isClaimedBrandRow = (target: ViewAsTarget): boolean => Boolean(target.impersonationName);

const firstRowPerAccount = (targets: ViewAsTarget[]): ViewAsTarget[] => {
  const seen = new Set<string>();
  return targets.filter((target) => {
    const key = viewAsAccountKey(target);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Filter and order the roster for a query: accounts, then brands with no
 * account, each group by match quality and then alphabetically,
 * case-insensitively. One row per account: browsing shows the account's own
 * name, so a claimed brand's row cannot land among the accounts when the brand
 * directory answers late, and a search shows whichever name matched best. Names
 * are disambiguated here, over the rows actually listed.
 */
export const rankViewAsTargets = (targets: ViewAsTarget[], rawNeedle: string): ViewAsTarget[] => {
  const needle = rawNeedle.trim().toLowerCase();
  if (!needle) {
    const ownNamesFirst = [...targets].sort(
      (a, b) => Number(isClaimedBrandRow(a)) - Number(isClaimedBrandRow(b)),
    );
    return disambiguateNames(
      firstRowPerAccount(ownNamesFirst).sort((a, b) => compareGroups(a, b) || compareNames(a, b)),
    );
  }
  const ranked = targets
    .filter((target) => matchesViewAsQuery(target, needle))
    .map((target) => ({ target, rank: viewAsMatchRank(target, needle) }))
    .sort((a, b) => a.rank - b.rank || compareNames(a.target, b.target))
    .map(({ target }) => target);
  return disambiguateNames(firstRowPerAccount(ranked).sort(compareGroups));
};

/**
 * The first `limit` rows, plus the account the admin highlighted if a late match
 * pushed it past the cut, so it cannot drop out from under them.
 */
export const capViewAsRows = (
  ranked: ViewAsTarget[],
  limit: number,
  keepAccountKey: string | null,
): ViewAsTarget[] => {
  const capped = ranked.slice(0, limit);
  if (!keepAccountKey || capped.some((target) => viewAsAccountKey(target) === keepAccountKey)) {
    return capped;
  }
  const kept = ranked.find((target) => viewAsAccountKey(target) === keepAccountKey);
  return kept ? [...capped.slice(0, limit - 1), kept] : capped;
};
